import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  MultiSelect,
  Pagination,
  Select,
  Skeleton,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ApiErrorAlert } from "../components/ApiErrorAlert.js";
import { QuadrantBadge } from "../components/QuadrantBadge.js";
import { StatusBadge } from "../components/StatusBadge.js";
import { CreateVisitModal } from "../components/visits/CreateVisitModal.js";
import { useCampaigns } from "../hooks/useCampaigns.js";
import {
  useAddNote,
  useCustomer,
  useCustomers,
  type CustomerListItem,
} from "../hooks/useCustomers.js";
import { BrandingPanel } from "../components/customers/BrandingPanel.js";
import { EngagementsCard } from "../components/EngagementsCard.js";
import { CustomerSystemsCard } from "../components/maintenance/CustomerSystemsCard.js";
import { ProjectsTable } from "../components/ProjectsTable.js";
import { CustomerNoteItem } from "../components/CustomerNoteItem.js";
import { hasRole, useCurrentUser } from "../hooks/useAuth.js";
import { useEmployees } from "../hooks/useEmployees.js";
import { useSegments } from "../hooks/useSegments.js";
import { LEAD_PALETTE } from "../theme.js";

// ── Helper ───────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "–";
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// ── Search-Param Validation ──────────────────────────────────────────────────

export interface CustomersSearch {
  withoutLead?: boolean;
}

export function validateCustomersSearch(
  raw: Record<string, unknown>,
): CustomersSearch {
  return {
    withoutLead: raw.withoutLead === true || raw.withoutLead === "true"
      ? true
      : undefined,
  };
}

/**
 * Reiter der Kundenakte. Die Reihenfolge entspricht der Reiterleiste; der
 * erste Eintrag ist zugleich der Rückfall, wenn die URL keinen (oder einen
 * unbekannten) Reiter trägt.
 */
export const CUSTOMER_DETAIL_TABS = [
  "stamm",
  "komponenten",
  "systeme",
  "umsatz",
  "termine",
  "kampagnen",
  "notizen",
  "praesentation",
] as const;

export type CustomerDetailTab = (typeof CUSTOMER_DETAIL_TABS)[number];

/**
 * Befund U9: Der aktive Reiter lag bisher nur im Komponentenzustand. Damit
 * ging er bei jedem Rücksprung verloren (Browser-Zurück landete stets auf
 * „Stammdaten") und ein einzelnes System war nicht verlinkbar. Beides steht
 * jetzt in der URL — `tab` wählt den Reiter, `systemId` klappt im
 * Systeme-Reiter genau ein System auf (Deep-Link von der Wartungsseite,
 * Befund U8).
 */
export interface CustomerDetailSearch {
  tab?: CustomerDetailTab;
  systemId?: string;
}

export function validateCustomerDetailSearch(
  raw: Record<string, unknown>,
): CustomerDetailSearch {
  const tab = raw.tab;
  const systemId = raw.systemId;
  return {
    tab:
      typeof tab === "string" &&
      (CUSTOMER_DETAIL_TABS as readonly string[]).includes(tab)
        ? (tab as CustomerDetailTab)
        : undefined,
    systemId:
      typeof systemId === "string" && systemId.length > 0 ? systemId : undefined,
  };
}

// ── Customers List Page ──────────────────────────────────────────────────────

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const columnHelper = createColumnHelper<CustomerListItem>();

function useCustomerColumns() {
  const { t } = useTranslation();
  return useMemo(
    () => [
      columnHelper.accessor("name", {
        header: t("pages.customers.tableKunde"),
        cell: (info) => <Text fw={500}>{info.getValue()}</Text>,
      }),
      columnHelper.accessor("segments", {
        header: t("pages.customers.tableSegmente"),
        cell: (info) => (
          <Group gap={4}>
            {info.getValue().map((s) => (
              <Badge key={s.id} color="blue" variant="light" size="sm">
                {s.name}
              </Badge>
            ))}
          </Group>
        ),
      }),
      columnHelper.accessor("bcgQuadrant", {
        header: t("pages.customers.tableBcg"),
        cell: (info) => {
          const row = info.row.original;
          // Eindeutiger Quadrant über alle Segmente → eine Badge; sonst pro
          // Segment (Splits wie IT-HR „Star" + IT-Security „Dog" bleiben sichtbar).
          if (row.bcgQuadrant || row.segments.length <= 1) {
            return (
              <QuadrantBadge
                quadrant={row.bcgQuadrant ?? row.segments[0]?.quadrant ?? null}
              />
            );
          }
          return (
            <Stack gap={2}>
              {row.segments.map((s) => (
                <Group key={s.id} gap={4} wrap="nowrap">
                  <QuadrantBadge quadrant={s.quadrant ?? null} />
                  <Text size="xs" c="dimmed">
                    {s.name}
                  </Text>
                </Group>
              ))}
            </Stack>
          );
        },
      }),
      columnHelper.accessor((row) => row.segments, {
        id: "leads",
        header: t("pages.customers.tableLead"),
        cell: (info) => {
          const segs = info.getValue();
          const leads = segs.flatMap((s) =>
            s.leads.map((l) => `${l.fullName} (${s.name})`),
          );
          if (leads.length === 0) return <Text c="dimmed">{t("common.dashed")}</Text>;
          return (
            <Text size="sm" lineClamp={2}>
              {leads.join(", ")}
            </Text>
          );
        },
      }),
      columnHelper.accessor("lastOnsiteVisitDate", {
        header: t("pages.customers.tableLastVisit"),
        cell: (info) => formatDate(info.getValue()),
      }),
    ],
    [t],
  );
}

export function CustomersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const search = useSearch({ strict: false }) as CustomersSearch;
  const columns = useCustomerColumns();

  // Default-Filter: Bereichsleiter sieht nur eigenes Segment (falls bekannt)
  const defaultSegment =
    hasRole(user, "bereichsleiter") && user?.defaultSegmentId
      ? [user.defaultSegmentId]
      : [];

  const [selectedSegments, setSelectedSegments] = useState<string[]>(defaultSegment);
  const [searchInput, setSearchInput] = useState("");
  const [letter, setLetter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [withoutLead, setWithoutLead] = useState(Boolean(search.withoutLead));
  const [withUnassigned, setWithUnassigned] = useState(false);
  const [zeigeAbgaenge, setZeigeAbgaenge] = useState(false);

  const [debouncedSearch] = useDebouncedValue(searchInput, 300);

  const { data: allSegments } = useSegments();
  const segmentOptions = (allSegments ?? []).map((s) => ({
    value: s.id,
    label: s.name,
  }));

  const { data, isLoading, isError } = useCustomers({
    segmentId: selectedSegments.length > 0 ? selectedSegments : undefined,
    q: debouncedSearch || undefined,
    letter: letter ?? undefined,
    page,
    withoutLead: withoutLead || undefined,
    withUnassignedProjects: withUnassigned || undefined,
    includeInactive: zeigeAbgaenge || undefined,
  });

  const table = useReactTable({
    data: data?.items ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 1;

  return (
    <Stack gap="md">
      <Title order={2}>{t("pages.customers.title")}</Title>

      {/* Filter Bar */}
      <Group gap="sm" align="center">
        <TextInput
          placeholder={t("pages.customers.searchPlaceholder")}
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.currentTarget.value);
            setPage(1);
          }}
          style={{ flex: 1, maxWidth: 320 }}
        />
        <MultiSelect
          placeholder={t("pages.customers.segmentFilter")}
          data={segmentOptions}
          value={selectedSegments}
          onChange={(v) => {
            setSelectedSegments(v);
            setPage(1);
          }}
          style={{ minWidth: 200 }}
          clearable
        />
        {hasRole(user, ["bereichsleiter", "stellvertreter", "admin", "geschaeftsleitung"]) && (
          <Checkbox
            label={t("pages.customers.withoutLeadCheckbox")}
            checked={withoutLead}
            onChange={(e) => {
              setWithoutLead(e.currentTarget.checked);
              setPage(1);
            }}
          />
        )}
        {hasRole(user, ["bereichsleiter", "stellvertreter", "admin", "geschaeftsleitung"]) && (
          <Checkbox
            label={t("pages.customers.withUnassignedCheckbox")}
            checked={withUnassigned}
            onChange={(e) => {
              setWithUnassigned(e.currentTarget.checked);
              setPage(1);
            }}
          />
        )}
        {/*
          Abgänge stehen seit der Zeitraum-Regel im Bestand, damit ihr im
          laufenden Jahr verdienter Umsatz nicht aus den Zahlen faellt. In der
          Liste sind sie aus — sonst wuerde die tägliche Arbeit mit Kunden
          zugestellt, die niemand mehr betreut.
        */}
        <Checkbox
          label={t("pages.customers.abgaengeCheckbox")}
          checked={zeigeAbgaenge}
          onChange={(e) => {
            setZeigeAbgaenge(e.currentTarget.checked);
            setPage(1);
          }}
        />
      </Group>

      {/* Alphabet-Filterleiste */}
      <Group gap={4} align="center">
        <Button
          variant={letter === null ? "filled" : "subtle"}
          size="compact-sm"
          onClick={() => {
            setLetter(null);
            setPage(1);
          }}
        >
          {t("pages.customers.alphabetAll")}
        </Button>
        {ALPHABET.map((ch) => (
          <Button
            key={ch}
            variant={letter === ch ? "filled" : "subtle"}
            size="compact-sm"
            px={8}
            onClick={() => {
              setLetter(letter === ch ? null : ch);
              setPage(1);
            }}
          >
            {ch}
          </Button>
        ))}
      </Group>

      {/* Error */}
      {isError && (
        <Alert color="red" title={t("common.error")}>
          {t("common.errorLoading")}
        </Alert>
      )}

      {/* Table */}
      <Box style={{ overflowX: "auto" }}>
        <Table highlightOnHover striped withTableBorder withColumnBorders>
          <Table.Thead>
            {table.getHeaderGroups().map((hg) => (
              <Table.Tr key={hg.id}>
                {hg.headers.map((header) => (
                  <Table.Th key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </Table.Th>
                ))}
              </Table.Tr>
            ))}
          </Table.Thead>
          <Table.Tbody>
            {isLoading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <Table.Tr key={i}>
                    {columns.map((_, j) => (
                      <Table.Td key={j}>
                        <Skeleton height={18} radius="sm" />
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))
              : table.getRowModel().rows.map((row) => (
                  <Table.Tr
                    key={row.id}
                    onClick={() =>
                      void navigate({
                        to: "/customers/$id",
                        params: { id: row.original.id },
                      })
                    }
                    style={{ cursor: "pointer" }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Table.Td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))}
            {!isLoading && (data?.items.length ?? 0) === 0 && (
              <Table.Tr>
                <Table.Td colSpan={columns.length}>
                  <Text c="dimmed" ta="center" py="md">
                    {t("pages.customers.empty")}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Group justify="center">
          <Pagination total={totalPages} value={page} onChange={setPage} />
        </Group>
      )}
    </Stack>
  );
}

// ── Customer Detail Page ─────────────────────────────────────────────────────

export function CustomerDetailPage({ id }: { id: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as CustomerDetailSearch;
  const activeTab: CustomerDetailTab = search.tab ?? "stamm";
  const { data, isLoading, isError, error } = useCustomer(id);
  const { user } = useCurrentUser();
  const [noteText, setNoteText] = useState("");
  const [noteSegmentId, setNoteSegmentId] = useState<string | null>(null);
  const [noteCrossSegment, setNoteCrossSegment] = useState(false);
  const addNote = useAddNote(id);
  const { data: employees } = useEmployees();
  const { data: activeCampaigns } = useCampaigns({ status: "active" });
  const [createVisitOpen, setCreateVisitOpen] = useState(false);

  if (isError) {
    // Befund G2: Wer aus der BCG-Matrix auf einen fremden Kunden klickt,
    // bekam hier „Daten konnten nicht geladen werden" — technisch für einen
    // Berechtigungsfall und ohne Rückweg. Die Unterscheidung trifft jetzt
    // zentral `ApiErrorAlert`, damit andere Detailseiten mitprofitieren.
    return (
      <ApiErrorAlert
        error={error}
        backTo="/customers"
        backLabel={t("pages.customers.title")}
      />
    );
  }

  // Dieselbe Menge, die `assertSegmentManager` im Server durchlaesst — die
  // Geschaeftsleitung fehlte hier, sah also eine Leseansicht, obwohl der Server
  // ihre Aenderung angenommen haette.
  const canEditPl = hasRole(user, [
    "bereichsleiter",
    "stellvertreter",
    "geschaeftsleitung",
    "admin",
  ]);
  // Kundendesign (D10): zusätzlich der Service Lead einer Beauftragung des
  // Kunden — er richtet den Termin ein. Dieselbe Regel wie der Server.
  const canEditBranding =
    canEditPl ||
    (user !== null && (data?.engagements ?? []).some((e) => e.leadEmployeeId === user.employeeId));

  return (
    <Stack gap="md">
      {/* Rückweg zur Liste — Detailseiten sollen keine Sackgassen sein. */}
      <Anchor component={Link} to="/customers" size="sm" c="dimmed">
        ← {t("pages.customers.title")}
      </Anchor>
      {isLoading ? (
        <>
          <Skeleton height={32} width={280} />
          <Skeleton height={20} width={180} />
        </>
      ) : (
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Title order={2}>{data?.name}</Title>
            <Text c="dimmed" size="sm">
              {t("pages.customers.detail.salesforceAccountIdLabel")}: {data?.salesforceAccountId}
            </Text>
          </Stack>
          <Group gap={4}>
            {data?.segments.map((s) => (
              <Badge key={s.id} color="blue" variant="light">
                {s.name}
              </Badge>
            ))}
          </Group>
        </Group>
      )}

      {/* Der Reiter wird per `replace` in die URL geschrieben, nicht per
          Push: Der Weg von der Wartungsliste in die Kundenakte und zurück ist
          der Hauptweg (Befund U8/U9) — ein Push je Reiterklick würde den
          Zurück-Knopf durch die Reiterhistorie führen statt zurück zur Liste.
          Der Deep-Link selbst ist ein normaler Push und bleibt damit ein
          Zurück-Schritt entfernt. */}
      <Tabs
        value={activeTab}
        onChange={(value) => {
          const next =
            typeof value === "string" &&
            (CUSTOMER_DETAIL_TABS as readonly string[]).includes(value)
              ? (value as CustomerDetailTab)
              : "stamm";
          void navigate({
            to: "/customers/$id",
            params: { id },
            // `systemId` gehört zum Systeme-Reiter — beim Wechsel auf einen
            // anderen Reiter würde er sonst als toter Parameter mitwandern.
            search: next === "systeme" ? { tab: next, systemId: search.systemId } : { tab: next },
            replace: true,
          });
        }}
      >
        <Tabs.List>
          <Tabs.Tab value="stamm">{t("pages.customers.detail.tabStamm")}</Tabs.Tab>
          <Tabs.Tab value="komponenten">{t("pages.customers.detail.tabKomponenten")}</Tabs.Tab>
          <Tabs.Tab value="systeme">{t("pages.customers.detail.tabSysteme")}</Tabs.Tab>
          <Tabs.Tab value="umsatz">{t("pages.customers.detail.tabUmsatz")}</Tabs.Tab>
          <Tabs.Tab value="termine">{t("pages.customers.detail.tabTermine")}</Tabs.Tab>
          <Tabs.Tab value="kampagnen">{t("pages.customers.detail.tabKampagnen")}</Tabs.Tab>
          <Tabs.Tab value="notizen">{t("pages.customers.detail.tabNotizen")}</Tabs.Tab>
          <Tabs.Tab value="praesentation">{t("pages.customers.detail.tabPraesentation")}</Tabs.Tab>
        </Tabs.List>

        {/* ── Tab 1: Stamm ──────────────────────────────────────────── */}
        <Tabs.Panel value="stamm" pt="md">
          {isLoading ? (
            <Stack gap="sm">
              <Skeleton height={20} width={200} />
              <Skeleton height={20} width={160} />
              <Skeleton height={20} width={240} />
            </Stack>
          ) : (
            <Stack gap="md">
              <Box>
                <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={6}>
                  {t("pages.customers.detail.engagementsTitle")}
                </Text>
                <EngagementsCard
                  customerId={id}
                  segments={data?.segments ?? []}
                  engagements={data?.engagements ?? []}
                  employees={employees ?? []}
                  canEdit={canEditPl}
                />
              </Box>

              <Box>
                <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={6}>
                  {t("pages.customers.detail.projectsTitle")}
                </Text>
                {(data?.serviceProjects.length ?? 0) === 0 ? (
                  <Text c="dimmed">{t("pages.customers.detail.noProjects")}</Text>
                ) : (
                  <ProjectsTable
                    customerId={id}
                    projects={data?.serviceProjects ?? []}
                    engagements={data?.engagements ?? []}
                    canEdit={canEditPl}
                  />
                )}
              </Box>
            </Stack>
          )}
        </Tabs.Panel>

        {/* ── Tab 2: Komponenten ────────────────────────────────────── */}
        <Tabs.Panel value="komponenten" pt="md">
          {isLoading ? (
            <Stack gap="xs">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height={20} />
              ))}
            </Stack>
          ) : (data?.components.length ?? 0) === 0 ? (
            <Text c="dimmed">{t("pages.customers.detail.noComponents")}</Text>
          ) : (
            <Table withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("pages.customers.detail.tableComponentName")}</Table.Th>
                  <Table.Th>{t("pages.customers.detail.tableComponentCategory")}</Table.Th>
                  <Table.Th>{t("pages.customers.detail.tableComponentStatus")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data?.components.map((c) => (
                  <Table.Tr key={c.id}>
                    <Table.Td>{c.name}</Table.Td>
                    <Table.Td>{c.category ?? t("common.dashed")}</Table.Td>
                    <Table.Td>
                      <StatusBadge status={c.status} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Tabs.Panel>

        {/* ── Tab: Systeme ──────────────────────────────────────────── */}
        <Tabs.Panel value="systeme" pt="md">
          <CustomerSystemsCard customerId={id} focusSystemId={search.systemId} />
        </Tabs.Panel>

        {/* ── Tab 3: Umsatz ────────────────────────────────────────── */}
        <Tabs.Panel value="umsatz" pt="md">
          {isLoading ? (
            <Skeleton height={260} />
          ) : (data?.revenues.length ?? 0) === 0 ? (
            <Text c="dimmed">{t("pages.customers.detail.noRevenueLong")}</Text>
          ) : (
            <RevenueChart data={data?.revenues ?? []} />
          )}
        </Tabs.Panel>

        {/* ── Tab 4: Termine ───────────────────────────────────────── */}
        <Tabs.Panel value="termine" pt="md">
          <Stack gap="md">
            <Group justify="flex-end">
              <Button size="sm" onClick={() => setCreateVisitOpen(true)}>
                {t("pages.customers.detail.createVisitButton")}
              </Button>
            </Group>
            {isLoading ? (
              <Stack gap="xs">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} height={60} radius="sm" />
                ))}
              </Stack>
            ) : (data?.recentVisits.length ?? 0) === 0 ? (
              <Text c="dimmed">{t("pages.customers.detail.noVisits")}</Text>
            ) : (
              <VisitsBySegment
                visits={data?.recentVisits ?? []}
                segments={data?.segments ?? []}
              />
            )}
          </Stack>
        </Tabs.Panel>

        {/* ── Tab 5: Kampagnen ─────────────────────────────────────── */}
        <Tabs.Panel value="kampagnen" pt="md">
          {isLoading ? (
            <Stack gap="xs">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} height={20} />
              ))}
            </Stack>
          ) : (data?.activeCampaigns.length ?? 0) === 0 ? (
            <Text c="dimmed">{t("pages.customers.detail.noCampaigns")}</Text>
          ) : (
            <Table withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("pages.customers.detail.tableCampaignTitle")}</Table.Th>
                  <Table.Th>{t("pages.customers.detail.tableCampaignStatus")}</Table.Th>
                  <Table.Th>{t("pages.customers.detail.tableCampaignRelevant")}</Table.Th>
                  <Table.Th>{t("pages.customers.detail.tableCampaignPresented")}</Table.Th>
                  <Table.Th>{t("pages.customers.detail.tableCampaignInstalled")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data?.activeCampaigns.map((p) => (
                  <Table.Tr key={p.id}>
                    <Table.Td>{p.campaignTitle}</Table.Td>
                    <Table.Td>
                      <StatusBadge status={p.campaignStatus} />
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge status={p.relevant} />
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge status={p.presented} />
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge status={p.installed} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Tabs.Panel>

        {/* ── Tab 6: Notizen ───────────────────────────────────────── */}
        <Tabs.Panel value="notizen" pt="md">
          {isLoading ? (
            <Stack gap="sm">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} height={80} radius="sm" />
              ))}
            </Stack>
          ) : (
            <Stack gap="md">
              {(data?.notes.length ?? 0) === 0 ? (
                <Text c="dimmed">{t("pages.customers.detail.noNotesLong")}</Text>
              ) : (
                <Stack gap="sm">
                  {data?.notes.map((n) => {
                    const seg = data.segments.find((s) => s.id === n.segmentId);
                    return (
                      <CustomerNoteItem
                        key={n.id}
                        note={n}
                        customerId={id}
                        segmentName={seg?.name ?? t("common.segment")}
                      />
                    );
                  })}
                </Stack>
              )}

              {/* Add note form */}
              <Box
                p="sm"
                style={(theme) => ({
                  border: `1px solid ${theme.colors.gray[3]}`,
                  borderRadius: theme.radius.sm,
                })}
              >
                <Stack gap="sm">
                  <Textarea
                    label={t("pages.customers.detail.newNoteLabel")}
                    placeholder={t("pages.customers.detail.newNotePlaceholder")}
                    value={noteText}
                    onChange={(e) => setNoteText(e.currentTarget.value)}
                    minRows={3}
                    autosize
                  />
                  <Group justify="space-between" align="flex-end">
                    <Group gap="md" align="flex-end">
                      <Select
                        label={t("pages.customers.detail.noteSegmentLabel")}
                        placeholder={t("pages.visits.createModal.segmentPlaceholder")}
                        required
                        value={noteSegmentId}
                        onChange={setNoteSegmentId}
                        data={(data?.segments ?? []).map((s) => ({
                          value: s.id,
                          label: s.name,
                        }))}
                        style={{ minWidth: 200 }}
                      />
                      <Checkbox
                        label={t("pages.customers.detail.noteCrossSegmentLabel")}
                        checked={noteCrossSegment}
                        onChange={(e) =>
                          setNoteCrossSegment(e.currentTarget.checked)
                        }
                      />
                    </Group>
                    <Button
                      mt="auto"
                      loading={addNote.isPending}
                      disabled={!noteText.trim() || !noteSegmentId}
                      onClick={() => {
                        if (!noteText.trim() || !noteSegmentId) return;
                        addNote.mutate(
                          {
                            note: noteText.trim(),
                            segmentId: noteSegmentId,
                            crossSegment: noteCrossSegment,
                          },
                          {
                            onSuccess: () => {
                              setNoteText("");
                              setNoteCrossSegment(false);
                            },
                          },
                        );
                      }}
                    >
                      {t("pages.customers.detail.noteSubmit")}
                    </Button>
                  </Group>
                </Stack>
              </Box>
            </Stack>
          )}
        </Tabs.Panel>

        {/* ── Tab 7: Präsentation (Kundendesign der Bühne, D10) ─────── */}
        {/* Nur der aktive Reiter lädt: Das Design braucht niemand, der die
            Umsätze ansieht. */}
        <Tabs.Panel value="praesentation" pt="md">
          {activeTab === "praesentation" && (
            <BrandingPanel customerId={id} customerName={data?.name ?? ""} canEdit={canEditBranding} />
          )}
        </Tabs.Panel>
      </Tabs>

      <CreateVisitModal
        opened={createVisitOpen}
        onClose={() => setCreateVisitOpen(false)}
        prefilledCustomerId={id}
        prefilledCustomerName={data?.name}
        employees={(employees ?? []).map((e) => ({
          value: e.id,
          label: e.fullName,
        }))}
        campaigns={(activeCampaigns ?? []).map((c) => ({
          value: c.id,
          label: c.title,
        }))}
      />
    </Stack>
  );
}

// ── Sub-Components ───────────────────────────────────────────────────────────

interface RevenueChartProps {
  data: Array<{
    segmentId: string;
    segmentName: string;
    /** First day of month, ISO date */
    yearMonth: string;
    /** Decimal string ("60000.00") */
    amountEur: string;
  }>;
}

/** Format "2026-04-01" → "Q2 2026" */
function quarterLabel(isoMonthStart: string): string {
  const d = new Date(isoMonthStart);
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `Q${q} ${d.getFullYear()}`;
}

function RevenueChart({ data }: RevenueChartProps) {
  const { t } = useTranslation();
  // Aggregate monthly → quarterly per segment
  const segments = [...new Set(data.map((d) => d.segmentName))];

  const chartData = Object.values(
    data.reduce<Record<string, Record<string, number | string>>>((acc, d) => {
      const label = quarterLabel(d.yearMonth);
      const amount = parseFloat(d.amountEur);
      const existing = acc[label];
      if (!existing) {
        acc[label] = { label, [d.segmentName]: amount };
      } else {
        existing[d.segmentName] = ((existing[d.segmentName] as number) ?? 0) + amount;
      }
      return acc;
    }, {}),
  ).slice(-8);

  const COLORS = LEAD_PALETTE;

  return (
    <Box>
      <Text size="sm" c="dimmed" mb="sm">
        {t("pages.customers.detail.revenueChartTitle")}
      </Text>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData} margin={{ top: 8, right: 16, left: 16, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={{ fontSize: 12 }} />
          <YAxis
            tickFormatter={(v: number) =>
              v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)
            }
            tick={{ fontSize: 12 }}
          />
          <Tooltip
            formatter={(value: number) =>
              new Intl.NumberFormat("de-DE", {
                style: "currency",
                currency: "EUR",
                maximumFractionDigits: 0,
              }).format(value)
            }
          />
          {segments.length > 1 && <Legend />}
          {segments.map((seg, i) => (
            <Bar key={seg} dataKey={seg} fill={COLORS[i % COLORS.length]} radius={[2, 2, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

interface VisitItem {
  id: string;
  scheduledAt: string;
  status: "planned" | "done" | "postponed" | "cancelled";
  shortMinutes: string | null;
  segmentId: string;
}

interface VisitsBySegmentProps {
  visits: VisitItem[];
  segments: Array<{ id: string; name: string }>;
}

/**
 * Gruppiert die Termine eines Kunden nach Segment (T11 — pro (Kunde × Segment)).
 * Innerhalb jeder Segment-Sektion: nächster geplanter Termin + Tabelle der letzten 10.
 * Segmente ohne Termine werden ausgeblendet, um Rauschen zu vermeiden.
 */
function VisitsBySegment({ visits, segments }: VisitsBySegmentProps) {
  const { t } = useTranslation();
  const visitsBySegment = new Map<string, VisitItem[]>();
  for (const v of visits) {
    const existing = visitsBySegment.get(v.segmentId) ?? [];
    existing.push(v);
    visitsBySegment.set(v.segmentId, existing);
  }

  const segmentNameById = new Map(segments.map((s) => [s.id, s.name]));

  // Reihenfolge: Segmente nach Name; danach Termine ohne (unbekannten) Segment-Eintrag.
  const orderedSegmentIds = [...visitsBySegment.keys()].sort((a, b) => {
    const an = segmentNameById.get(a) ?? "";
    const bn = segmentNameById.get(b) ?? "";
    return an.localeCompare(bn);
  });

  return (
    <Stack gap="lg">
      {orderedSegmentIds.map((segId) => {
        const segName = segmentNameById.get(segId) ?? t("pages.customers.detail.unknownSegment");
        const segVisits = visitsBySegment.get(segId) ?? [];
        const sorted = [...segVisits].sort(
          (a, b) =>
            new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime(),
        );
        const nextPlanned = sorted.find(
          (v) => v.status === "planned" && new Date(v.scheduledAt) >= new Date(),
        );
        const last10 = sorted.slice(0, 10);

        return (
          <Stack key={segId} gap="sm">
            <Group gap="xs">
              <Badge color="blue" variant="light" size="md">
                {segName}
              </Badge>
              <Text size="xs" c="dimmed">
                {t("pages.customers.detail.visitsCount", { count: segVisits.length })}
              </Text>
            </Group>
            {nextPlanned && (
              <Box
                p="sm"
                style={(theme) => ({
                  border: `2px solid ${theme.colors.blue[4]}`,
                  borderRadius: theme.radius.sm,
                  background: theme.colors.blue[0],
                })}
              >
                <Group justify="space-between">
                  <Group gap="sm">
                    <StatusBadge status="planned" />
                    <Text fw={600} size="sm">
                      {t("pages.customers.detail.nextVisit", { date: formatDate(nextPlanned.scheduledAt) })}
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {nextPlanned.shortMinutes ?? ""}
                  </Text>
                </Group>
              </Box>
            )}

            <Table withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("pages.customers.detail.visitsTableDate")}</Table.Th>
                  <Table.Th>{t("pages.customers.detail.visitsTableStatus")}</Table.Th>
                  <Table.Th>{t("pages.customers.detail.visitsTableMinutes")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {last10.map((v) => (
                  <Table.Tr key={v.id}>
                    <Table.Td>{formatDate(v.scheduledAt)}</Table.Td>
                    <Table.Td>
                      <StatusBadge
                        status={v.status === "planned" ? "planned" : v.status}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{v.shortMinutes ?? t("common.dashed")}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Stack>
        );
      })}
    </Stack>
  );
}
